import { create } from 'zustand';
import {
  getRunAnalysis,
  updateRunAnalysis,
  getRunAnalysisUIPermissions,
} from '@services/runAnalysisApi.service';
import type {
  RunAnalysisEntity,
  RunAnalysisUIPermissions,
  RunAnalysisUpdatePayload,
} from '@/types/run-stage.types';

type RunAnalysisStatus = 'idle' | 'loading' | 'ready' | 'error';

interface RunAnalysisStoreState {
  analysisId: string | null;
  analysis: RunAnalysisEntity | null;
  uiPermissions: RunAnalysisUIPermissions | null;
  status: RunAnalysisStatus;
  error: string | null;
  lastLoadedAt: number | null;
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  selectedCloneNumber: number | null;

  // Actions
  clear: () => void;
  hydrate: (analysisId: string) => Promise<RunAnalysisEntity>;
  refresh: () => Promise<RunAnalysisEntity | null>;
  fetchUIPermissions: (analysisId: string) => Promise<RunAnalysisUIPermissions | null>;
  update: (payload: RunAnalysisUpdatePayload) => Promise<RunAnalysisEntity>;
  setSelectedNode: (nodeId: string | null) => void;
  setSelectedEdge: (edgeId: string | null) => void;
  setSelectedCloneNumber: (cloneNumber: number | null) => void;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export const useRunAnalysisStore = create<RunAnalysisStoreState>((set, get) => ({
  analysisId: null,
  analysis: null,
  uiPermissions: null,
  status: 'idle',
  error: null,
  lastLoadedAt: null,
  selectedNodeId: null,
  selectedEdgeId: null,
  selectedCloneNumber: null,

  clear: () => {
    set({
      analysisId: null,
      analysis: null,
      uiPermissions: null,
      status: 'idle',
      error: null,
      lastLoadedAt: null,
      selectedNodeId: null,
      selectedEdgeId: null,
      selectedCloneNumber: null,
    });
  },

  hydrate: async (analysisId: string) => {
    set({ analysisId, status: 'loading', error: null });
    try {
      const [analysis, permissions] = await Promise.all([
        getRunAnalysis(analysisId),
        getRunAnalysisUIPermissions(analysisId),
      ]);

      set({
        analysis,
        uiPermissions: permissions,
        status: 'ready',
        error: null,
        lastLoadedAt: Date.now(),
        // A different analysis means different batch instances; let the graph
        // re-derive and pick the first one.
        selectedCloneNumber: null,
      });

      return analysis;
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to load RunAnalysis');
      console.error(`[runAnalysisStore] hydrate error:`, errorMessage);
      set({
        status: 'error',
        error: errorMessage,
      });
      throw error;
    }
  },

  refresh: async () => {
    const { analysisId } = get();
    if (!analysisId) return null;

    try {
      const analysis = await getRunAnalysis(analysisId);
      set({ analysis, lastLoadedAt: Date.now() });
      return analysis;
    } catch (error) {
      console.error('[RunAnalysisStore.refresh] Error:', error);
      return null;
    }
  },

  fetchUIPermissions: async (analysisId: string) => {
    try {
      const permissions = await getRunAnalysisUIPermissions(analysisId);
      set({ uiPermissions: permissions });
      return permissions;
    } catch (error) {
      console.error('[RunAnalysisStore.fetchUIPermissions] Error:', error);
      return null;
    }
  },

  update: async (payload: RunAnalysisUpdatePayload) => {
    try {
      const updated = await updateRunAnalysis(payload);
      set((state) => {
        // Some update endpoints return partial entities (e.g. status-only
        // responses). Merge onto existing analysis so comments and other
        // unchanged fields do not disappear until a full refresh.
        const mergedAnalysis = state.analysis
          ? {
              ...state.analysis,
              ...updated,
            }
          : updated;

        return { analysis: mergedAnalysis };
      });
      return updated;
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to update RunAnalysis');
      set({ error: errorMessage });
      throw error;
    }
  },

  setSelectedNode: (nodeId: string | null) => {
    set({ selectedNodeId: nodeId, selectedEdgeId: null });
  },

  setSelectedEdge: (edgeId: string | null) => {
    set({ selectedEdgeId: edgeId, selectedNodeId: null });
  },

  setSelectedCloneNumber: (selectedCloneNumber: number | null) => {
    set({ selectedCloneNumber });
  },
}));
